import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, Link } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const ShippingAreaForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [regions, setRegions] = useState([]);
    const [region, setRegion] = useState("");
    const [name, setName] = useState("");
    const [deliveryDays, setDeliveryDays] = useState("");
    const [minOrderValue, setMinOrderValue] = useState(500);
    const [shippingCharge, setShippingCharge] = useState(80);
    const [active, setActive] = useState(true);

    useEffect(() => {
        const fetchRegions = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/regions');
                const names = data.regions.map((r) => r.name);
                setRegions(names);
                if (!isEditMode && names.length > 0) {
                    setRegion(names[0]);
                }
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load regions", { variant: "error" });
            }
        };

        const fetchArea = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/shipping-areas');
                const area = data.areas.find((a) => a._id === id);
                if (!area) {
                    enqueueSnackbar("Shipping area not found", { variant: "error" });
                    navigate('/admin/shipping-areas');
                    return;
                }
                setRegion(area.region);
                setName(area.name);
                setDeliveryDays(area.deliveryDays || "");
                setMinOrderValue(area.minOrderValue ?? 500);
                setShippingCharge(area.shippingCharge ?? 80);
                setActive(area.active);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load area", { variant: "error" });
            }
        };

        const run = async () => {
            await fetchRegions();
            if (isEditMode) await fetchArea();
            setLoading(false);
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();
        if (!name.trim()) return enqueueSnackbar("Please enter an area name", { variant: "warning" });

        const payload = { region, name, deliveryDays, minOrderValue, shippingCharge, active };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/shipping-area/${id}`, payload);
                enqueueSnackbar("Shipping area updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/shipping-area/new', payload);
                enqueueSnackbar("Shipping area created", { variant: "success" });
            }
            navigate('/admin/shipping-areas');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save area", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Shipping Area | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="shippingareaform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Shipping Area</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-full flex flex-col gap-1">
                        <TextField label="Region" select variant="outlined" size="small" fullWidth value={region} onChange={(e) => setRegion(e.target.value)}>
                            {regions.map((r) => <MenuItem value={r} key={r}>{r}</MenuItem>)}
                        </TextField>
                        <Link to="/admin/regions" className="text-xs text-primary-blue hover:underline self-start">+ Add a new region</Link>
                    </div>
                    <TextField label="Area Name" variant="outlined" size="small" required fullWidth value={name} onChange={(e) => setName(e.target.value)} />
                </div>

                <TextField
                    label="Delivery Days"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={deliveryDays}
                    onChange={(e) => setDeliveryDays(e.target.value)}
                    helperText="e.g. 'Tue/Thu/Sat' or 'Wednesday'"
                />

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Minimum Order Value (HK$)"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={minOrderValue}
                        onChange={(e) => setMinOrderValue(Number(e.target.value))}
                        helperText="Orders at or above this get free shipping"
                    />
                    <TextField
                        label="Shipping Charge (HK$)"
                        type="number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={shippingCharge}
                        onChange={(e) => setShippingCharge(Number(e.target.value))}
                        helperText="Charged when the order is below the minimum"
                    />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active (available for selection at checkout)"
                />

                <div className="flex justify-end">
                    <input form="shippingareaform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Area"} />
                </div>

            </form>
        </>
    );
};

export default ShippingAreaForm;
